import * as THREE from 'three'
import type { Cell } from './games/types'

export type CrtPhase = 'in' | 'on' | 'out'
export interface CrtTimings { melt: number; warm: number; out: number }

const MAX_DPR = 2
/** Fraction of the canvas on each side kept clear of the glass so the glow has room to spill. */
const MARGIN = 0.08

const VERT = `varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.,1.);}`

/**
 * The tube. A rounded, barrel-curved sheet of glass drawn in one fragment shader: the game
 * picture (a 2D canvas texture), scanlines, aperture grille, vignette, a reflection, a thin rim
 * and an outer neon glow. The Doom screen melt moves the whole thing: on the way in every
 * column drops from above with its own delay and lands in place, then the tube flashes on as a
 * bright line that opens up with a degauss wobble; on the way out the picture drips off the
 * bottom column by column.
 */
const FRAG = `
precision highp float;
uniform sampler2D tex;
uniform vec2 res;
uniform vec2 texRes;
uniform float t;
uniform float enter;   // 0..1 drip-in progress (1 = landed)
uniform float melt;    // 0..1 drip-out progress (0 = not started)
uniform float on;      // 0..1 tube warm-up (1 = picture stable)
varying vec2 vUv;

float hash(float n){return fract(sin(n*12.9898)*43758.5453);}
float noise(float x){float i=floor(x);float f=fract(x);f=f*f*(3.-2.*f);return mix(hash(i),hash(i+1.),f);}

// how far (0..1, with gravity) a column has fallen at wipe progress p. Columns get a smooth
// random delay so neighbours drip together, plus a little jitter so it isn't a clean wave.
float drip(float x,float p){
	float c=floor(x*150.);
	float d=noise(c*0.21)*0.55+hash(c+7.)*0.2;
	float k=clamp(p*1.75-d,0.,1.);
	return k*k;
}

void main(){
	vec2 uv=vec2(vUv.x,1.-vUv.y); // y down
	float s=0.;
	if(enter<1.)s=-(1.-drip(uv.x,enter))*1.3;
	if(melt>0.)s=drip(uv.x,melt)*1.3;
	vec2 q=vec2(uv.x,uv.y-s);
	vec2 g=(q-${MARGIN})/(1.-2.*${MARGIN});
	// barrel curvature: the outline bulges, the picture stretches towards the edges
	vec2 p=g*2.-1.;
	p.x*=1.+0.05*p.y*p.y;
	p.y*=1.+0.075*p.x*p.x;
	// rounded-box sdf in half-height units so corners and haze are round, not squashed. <0 inside the glass
	float ar=texRes.x/texRes.y;
	float r=0.16;
	vec2 dd=abs(p*vec2(ar,1.))-(vec2(ar,1.)-r);
	float d=length(max(dd,0.))-r;
	float aa=2.5/res.y;
	float inside=1.-smoothstep(-aa,aa,d);
	vec2 cuv=p*0.5+0.5;
	vec2 cc=clamp(cuv,0.,1.); // never feed pow() a negative: outside the glass it would go NaN
	// warm-up: the picture is a squashed bright line that opens to full height, wobbling as it settles
	float tube=step(0.0001,on);
	float e=smoothstep(0.,1.,on);
	float sq=mix(0.004,1.,e);
	vec2 su=vec2(cuv.x+0.014*(1.-e)*sin(cuv.y*38.+t*45.),(cuv.y-0.5)/sq+0.5);
	float band=step(0.,su.y)*step(su.y,1.)*tube;
	vec4 pic=texture2D(tex,vec2(su.x,1.-su.y))*band;
	vec3 col=pic.rgb*pic.a;
	col+=vec3(1.)*(1.-e)*(1.-e)*1.6*band; // the flash
	// glass backdrop
	vec2 cp=cuv-0.5;
	col+=mix(vec3(0.055,0.018,0.14),vec3(0.012,0.,0.035),smoothstep(0.,0.72,length(cp)));
	// scanlines + aperture grille (in picture pixels)
	col*=0.80+0.20*sin(su.y*texRes.y*1.5708);
	col*=0.92+0.08*sin(su.x*texRes.x*2.094);
	// vignette, rolling bar, flicker
	col*=pow(max(16.*cc.x*cc.y*(1.-cc.x)*(1.-cc.y),0.001),0.16);
	col*=1.+0.035*sin(cuv.y*7.-t*2.2);
	col*=0.985+0.015*sin(t*115.);
	// a soft reflection across the top-left of the glass
	col+=smoothstep(0.5,0.,abs(cuv.x*0.55+cuv.y-0.5))*0.05*(1.-cuv.y*0.6);
	// thin rim just inside the edge, like the glass catching the light
	float rim=smoothstep(-0.045,-0.018,d)*(1.-smoothstep(-0.018,0.,d));
	col+=rim*vec3(0.35,0.85,1.)*0.45;
	// neon haze outside the glass
	float glow=0.55*pow(clamp(1.-max(d,0.)/0.2,0.,1.),2.4)*(1.-inside);
	vec3 gc=mix(vec3(1.,0.17,0.84),vec3(0.13,0.94,1.),cc.y)*glow;
	gl_FragColor=vec4(col*inside+gc,min(1.,inside+glow)); // premultiplied
}`

/**
 * The play area. Game frames are drawn as glowing blocks into an offscreen 2D canvas, which
 * feeds the tube shader above. Phases (in → on → out) are timed here so the melt, the flash and
 * the game's hold all line up.
 */
export class CrtScreen {
	private renderer: THREE.WebGLRenderer
	private scene = new THREE.Scene()
	private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
	private material: THREE.ShaderMaterial
	private tex: THREE.CanvasTexture
	private pic = document.createElement('canvas')
	private ctx: CanvasRenderingContext2D
	private cells: Cell[] = []
	private W = 1
	private H = 1
	private sprites = new Map<string, HTMLCanvasElement>()
	private board: { key: string; img: HTMLCanvasElement } | null = null
	private phase: CrtPhase = 'in'
	private phaseAt = performance.now()
	private t0 = performance.now()
	private raf = 0
	private ro: ResizeObserver
	private reduce = matchMedia('(prefers-reduced-motion: reduce)').matches

	constructor(private canvas: HTMLCanvasElement, private timings: CrtTimings) {
		this.ctx = this.pic.getContext('2d')!
		this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false, premultipliedAlpha: true })
		this.renderer.setClearColor(0x000000, 0)
		this.tex = this.makeTexture()
		this.material = new THREE.ShaderMaterial({
			vertexShader: VERT,
			fragmentShader: FRAG,
			transparent: true,
			depthTest: false,
			depthWrite: false,
			blending: THREE.CustomBlending,
			blendSrc: THREE.OneFactor,
			blendDst: THREE.OneMinusSrcAlphaFactor,
			blendSrcAlpha: THREE.OneFactor,
			blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
			uniforms: {
				tex: { value: this.tex },
				res: { value: new THREE.Vector2(1, 1) },
				texRes: { value: new THREE.Vector2(1, 1) },
				t: { value: 0 },
				enter: { value: 0 },
				melt: { value: 0 },
				on: { value: 0 },
			},
		})
		this.scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material))
		this.ro = new ResizeObserver(() => this.resize())
		this.ro.observe(canvas)
		this.resize()
		this.raf = requestAnimationFrame(this.frame)
	}

	/** Draw a game frame. Cheap enough to call every tick. */
	draw(cells: Cell[], width: number, height: number): void {
		this.cells = cells
		this.W = width
		this.H = height
		this.paint()
	}

	setPhase(phase: CrtPhase): void {
		if (phase === this.phase) return
		this.phase = phase
		this.phaseAt = performance.now()
	}

	dispose(): void {
		cancelAnimationFrame(this.raf)
		this.ro.disconnect()
		this.tex.dispose()
		this.material.dispose()
		this.renderer.dispose()
	}

	/**
	 * Three allocates immutable storage for a canvas texture on its first upload and only ever
	 * sub-image updates it afterwards, so a resized picture canvas overflows it (Safari rejects
	 * every upload and the screen freezes). A fresh texture per size avoids that.
	 */
	private makeTexture(): THREE.CanvasTexture {
		const tex = new THREE.CanvasTexture(this.pic)
		tex.minFilter = tex.magFilter = THREE.LinearFilter
		tex.generateMipmaps = false
		return tex
	}

	// ---- tube ----

	private frame = (now: number): void => {
		this.raf = requestAnimationFrame(this.frame)
		const u = this.material.uniforms
		const since = now - this.phaseAt
		const { melt, warm, out } = this.timings
		u.t.value = (now - this.t0) / 1000
		if (this.reduce) {
			u.enter.value = 1
			u.on.value = 1
			u.melt.value = this.phase === 'out' ? 1 : 0
		} else {
			u.enter.value = this.phase === 'in' ? Math.min(1, since / melt) : 1
			u.on.value = this.phase === 'in' ? 0 : this.phase === 'on' ? Math.min(1, since / warm) : 1
			u.melt.value = this.phase === 'out' ? Math.min(1, since / out) : 0
		}
		this.renderer.render(this.scene, this.camera)
	}

	private resize(): void {
		// clientWidth ignores any transform, unlike getBoundingClientRect
		const dpr = Math.min(devicePixelRatio || 1, MAX_DPR)
		const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr))
		const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr))
		if (w === this.canvas.width && h === this.canvas.height) return
		this.renderer.setSize(w, h, false)
		this.material.uniforms.res.value.set(w, h)
		this.pic.width = Math.max(1, Math.round(w * (1 - 2 * MARGIN)))
		this.pic.height = Math.max(1, Math.round(h * (1 - 2 * MARGIN)))
		this.material.uniforms.texRes.value.set(this.pic.width, this.pic.height)
		this.tex.dispose()
		this.tex = this.makeTexture()
		this.material.uniforms.tex.value = this.tex
		this.sprites.clear()
		this.board = null
		this.paint()
	}

	// ---- picture ----

	private paint(): void {
		const ctx = this.ctx
		const cw = this.pic.width
		const ch = this.pic.height
		ctx.globalCompositeOperation = 'source-over'
		ctx.clearRect(0, 0, cw, ch)
		const cell = Math.floor(Math.min((cw * 0.92) / this.W, (ch * 0.92) / this.H))
		if (cell >= 2) {
			const ox = Math.round((cw - cell * this.W) / 2)
			const oy = Math.round((ch - cell * this.H) / 2)
			ctx.drawImage(this.boardImage(cell), ox, oy)
			// additive so overlapping halos bloom into each other
			ctx.globalCompositeOperation = 'lighter'
			for (const c of this.cells) ctx.drawImage(this.sprite(c.color, cell), ox + (c.x - 1) * cell, oy + (c.y - 1) * cell)
			ctx.globalCompositeOperation = 'source-over'
		}
		this.tex.needsUpdate = true
	}

	/** Phosphor dots at every cell plus a hairline around the board (snake cares about walls). Cached per size. */
	private boardImage(cell: number): HTMLCanvasElement {
		const key = `${this.W}x${this.H}@${cell}`
		if (this.board?.key === key) return this.board.img
		const img = document.createElement('canvas')
		img.width = cell * this.W
		img.height = cell * this.H
		const g = img.getContext('2d')!
		const d = Math.max(1, Math.round(cell * 0.12))
		g.fillStyle = 'rgba(155,127,199,.16)'
		for (let y = 0; y < this.H; y++) for (let x = 0; x < this.W; x++) g.fillRect(x * cell + (cell - d) / 2, y * cell + (cell - d) / 2, d, d)
		const lw = Math.max(1, cell * 0.08)
		g.lineWidth = lw
		g.strokeStyle = 'rgba(34,240,255,.22)'
		g.strokeRect(lw / 2, lw / 2, img.width - lw, img.height - lw)
		this.board = { key, img }
		return img
	}

	/** One glowing block, three cells wide so the halo has room. Cached per colour and size. */
	private sprite(color: number, cell: number): HTMLCanvasElement {
		const key = `${color}@${cell}`
		const hit = this.sprites.get(key)
		if (hit) return hit
		if (this.sprites.size > 512) this.sprites.clear()
		const size = cell * 3
		const s = document.createElement('canvas')
		s.width = s.height = size
		const g = s.getContext('2d')!
		const r = (color >> 16) & 255
		const gr = (color >> 8) & 255
		const b = color & 255
		const halo = g.createRadialGradient(size / 2, size / 2, cell * 0.3, size / 2, size / 2, size / 2)
		halo.addColorStop(0, `rgba(${r},${gr},${b},.55)`)
		halo.addColorStop(1, `rgba(${r},${gr},${b},0)`)
		g.fillStyle = halo
		g.fillRect(0, 0, size, size)
		const inset = Math.max(1, Math.round(cell * 0.09))
		const inner = cell - inset * 2
		g.fillStyle = `rgb(${r},${gr},${b})`
		g.fillRect(cell + inset, cell + inset, inner, inner)
		// a lighter top edge sells the "little glass block" look
		g.fillStyle = 'rgba(255,255,255,.28)'
		g.fillRect(cell + inset, cell + inset, inner, Math.max(1, Math.round(cell * 0.18)))
		this.sprites.set(key, s)
		return s
	}
}
